"use client";

import * as React from "react";
import {
  Plus,
  Trash2,
  AlertTriangle,
  Info,
  Repeat,
  Globe,
  Phone,
} from "lucide-react";
import { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateRangePicker } from "./date-range-picker";
import { SingleDatePicker } from "./single-date-picker";
import { format, parse } from "date-fns";
import {
  type DayOfWeek,
  type RRuleOptions,
  DAY_LABELS,
  ALL_DAYS,
  getDayOfWeek,
  describeRRule,
  buildRRule,
} from "@/utils/rrule-helpers";
import { cn } from "@/lib/utils";

// Timezone option type (from lookup.getTimezones)
export interface TimezoneOption {
  id: string;
  label: string;
}

// Recurrence settings for internal form state
interface RecurrenceSettings {
  enabled: boolean;
  freq: "DAILY" | "WEEKLY";
  interval: number;
  byDay: DayOfWeek[];
  endType: "count" | "until";
  count: number;
  until: string | null; // 'yyyy-MM-dd'
}

const defaultRecurrence: RecurrenceSettings = {
  enabled: false,
  freq: "WEEKLY",
  interval: 1,
  byDay: [],
  endType: "count",
  count: 4,
  until: null,
};

// Define the type for a single schedule entry in the form value
export interface ScheduleEntryValue {
  startDate: string | null; // 'yyyy-MM-dd' - single date for recurring, or start of range
  endDate: string | null; // 'yyyy-MM-dd' - end of range (only used when not recurring)
  startTime: string | null; // 'HH:mm'
  endTime: string | null; // 'HH:mm'
  recurrence: RecurrenceSettings;
  isOnCall?: boolean; // Whether this is an on-call schedule
}

export function ScheduleInput(props: {
  onChange: (value: ScheduleEntryValue[]) => void;
  value: ScheduleEntryValue[];
  timezoneId?: string;
  onTimezoneChange?: (timezoneId: string) => void;
  timezones?: TimezoneOption[];
}) {
  const handleAddSchedule = () => {
    const newSchedule: ScheduleEntryValue = {
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
      recurrence: { ...defaultRecurrence },
      isOnCall: false,
    };
    const updatedSchedules = [...props.value, newSchedule];
    props.onChange(updatedSchedules);
  };

  const handleRemoveSchedule = (index: number) => {
    const updatedSchedules = props.value.filter((_, i) => i !== index);
    props.onChange(updatedSchedules);
  };

  const handleDateChange = (
    scheduleIndex: number,
    dateRange: DateRange | undefined,
  ) => {
    const updatedSchedules = props.value.map((schedule, i) => {
      if (i === scheduleIndex) {
        const newStartDate = dateRange?.from
          ? format(dateRange.from, "yyyy-MM-dd")
          : null;
        const newEndDate = dateRange?.to
          ? format(dateRange.to, "yyyy-MM-dd")
          : null;

        // If recurrence is enabled and a date is selected, auto-set byDay to include that day
        let updatedRecurrence = schedule.recurrence;
        if (
          schedule.recurrence.enabled &&
          schedule.recurrence.freq === "WEEKLY" &&
          dateRange?.from
        ) {
          const dayOfWeek = getDayOfWeek(dateRange.from);
          if (!schedule.recurrence.byDay.includes(dayOfWeek)) {
            updatedRecurrence = {
              ...schedule.recurrence,
              byDay: [...schedule.recurrence.byDay, dayOfWeek],
            };
          }
        }

        return {
          ...schedule,
          startDate: newStartDate,
          endDate: newEndDate,
          recurrence: updatedRecurrence,
        };
      }
      return schedule;
    });
    props.onChange(updatedSchedules);
  };

  const handleSingleDateChange = (
    scheduleIndex: number,
    date: Date | undefined,
  ) => {
    const updatedSchedules = props.value.map((schedule, i) => {
      if (i === scheduleIndex) {
        const newStartDate = date ? format(date, "yyyy-MM-dd") : null;

        // Auto-set byDay for weekly recurrence
        let updatedRecurrence = schedule.recurrence;
        if (
          schedule.recurrence.enabled &&
          schedule.recurrence.freq === "WEEKLY" &&
          date
        ) {
          const dayOfWeek = getDayOfWeek(date);
          if (!schedule.recurrence.byDay.includes(dayOfWeek)) {
            updatedRecurrence = {
              ...schedule.recurrence,
              byDay: [dayOfWeek],
            };
          }
        }

        return {
          ...schedule,
          startDate: newStartDate,
          endDate: null, // Clear end date when using single date
          recurrence: updatedRecurrence,
        };
      }
      return schedule;
    });
    props.onChange(updatedSchedules);
  };

  const handleTimeChange = (
    scheduleIndex: number,
    type: "startTime" | "endTime",
    value: string,
  ) => {
    const updatedSchedules = props.value.map((schedule, i) =>
      i === scheduleIndex ? { ...schedule, [type]: value || null } : schedule,
    );
    props.onChange(updatedSchedules);
  };

  const handleRecurrenceToggle = (scheduleIndex: number, enabled: boolean) => {
    const updatedSchedules = props.value.map((schedule, i) => {
      if (i === scheduleIndex) {
        // When enabling recurrence, if we have a start date, auto-select that day
        let byDay: DayOfWeek[] = [];
        if (enabled && schedule.startDate) {
          const date = parse(schedule.startDate, "yyyy-MM-dd", new Date());
          byDay = [getDayOfWeek(date)];
        }

        return {
          ...schedule,
          endDate: enabled ? null : schedule.endDate, // Clear end date when enabling recurrence
          recurrence: {
            ...schedule.recurrence,
            enabled,
            byDay: enabled ? byDay : schedule.recurrence.byDay,
          },
        };
      }
      return schedule;
    });
    props.onChange(updatedSchedules);
  };

  const handleRecurrenceChange = (
    scheduleIndex: number,
    field: keyof RecurrenceSettings,
    value: RecurrenceSettings[keyof RecurrenceSettings],
  ) => {
    const updatedSchedules = props.value.map((schedule, i) => {
      if (i === scheduleIndex) {
        return {
          ...schedule,
          recurrence: {
            ...schedule.recurrence,
            [field]: value,
          },
        };
      }
      return schedule;
    });
    props.onChange(updatedSchedules);
  };

  const handleDayToggle = (scheduleIndex: number, day: DayOfWeek) => {
    const updatedSchedules = props.value.map((schedule, i) => {
      if (i === scheduleIndex) {
        const currentDays = schedule.recurrence.byDay;
        const newDays = currentDays.includes(day)
          ? currentDays.filter((d) => d !== day)
          : [...currentDays, day];
        return {
          ...schedule,
          recurrence: {
            ...schedule.recurrence,
            byDay: newDays,
          },
        };
      }
      return schedule;
    });
    props.onChange(updatedSchedules);
  };

  // Get RRULE description for display
  const getRRuleDescription = (schedule: ScheduleEntryValue): string => {
    if (!schedule.recurrence.enabled) {
      return "";
    }

    // For weekly recurrence with multiple days, "count" means weeks, not occurrences
    // So we multiply by the number of selected days to get actual occurrences
    let actualCount: number | undefined;
    if (schedule.recurrence.endType === "count") {
      if (
        schedule.recurrence.freq === "WEEKLY" &&
        schedule.recurrence.byDay.length > 1
      ) {
        actualCount =
          schedule.recurrence.count * schedule.recurrence.byDay.length;
      } else {
        actualCount = schedule.recurrence.count;
      }
    }

    const options: RRuleOptions = {
      freq: schedule.recurrence.freq,
      interval: schedule.recurrence.interval,
      byDay:
        schedule.recurrence.freq === "WEEKLY"
          ? schedule.recurrence.byDay
          : undefined,
      count: actualCount,
      until:
        schedule.recurrence.endType === "until" && schedule.recurrence.until
          ? parse(schedule.recurrence.until, "yyyy-MM-dd", new Date())
          : undefined,
    };

    const rrule = buildRRule(options);
    return rrule ? describeRRule(rrule) : "";
  };

  return (
    <div className="space-y-4">
      {props.value.map((schedule, scheduleIndex) => (
        <div
          key={scheduleIndex}
          className="p-4 border rounded-md relative group bg-background/50"
        >
          {/* Remove Button */}
          {props.value.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6"
              onClick={() => handleRemoveSchedule(scheduleIndex)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
              <span className="sr-only">Remove schedule</span>
            </Button>
          )}

          <div className="space-y-4">
            {/* Date Selection */}
            <div className="space-y-3">
              <Label>
                {schedule.recurrence.enabled ? "Start Date" : "Date Range"}
              </Label>
              {schedule.recurrence.enabled ? (
                <SingleDatePicker
                  date={
                    schedule.startDate
                      ? parse(schedule.startDate, "yyyy-MM-dd", new Date())
                      : undefined
                  }
                  onDateChange={(date) =>
                    handleSingleDateChange(scheduleIndex, date)
                  }
                />
              ) : (
                <DateRangePicker
                  date={{
                    from: schedule.startDate
                      ? parse(schedule.startDate, "yyyy-MM-dd", new Date())
                      : undefined,
                    to: schedule.endDate
                      ? parse(schedule.endDate, "yyyy-MM-dd", new Date())
                      : undefined,
                  }}
                  onDateChange={(range) =>
                    handleDateChange(scheduleIndex, range)
                  }
                />
              )}
            </div>

            {/* Time inputs and timezone */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Start Time */}
              <div className="space-y-1">
                <Label htmlFor={`${scheduleIndex}-startTime`}>Start Time</Label>
                <Input
                  id={`${scheduleIndex}-startTime`}
                  type="time"
                  value={schedule.startTime ?? ""}
                  onChange={(e) =>
                    handleTimeChange(scheduleIndex, "startTime", e.target.value)
                  }
                  className="w-full"
                />
              </div>

              {/* End Time */}
              <div className="space-y-1">
                <Label htmlFor={`${scheduleIndex}-endTime`}>End Time</Label>
                <Input
                  id={`${scheduleIndex}-endTime`}
                  type="time"
                  value={schedule.endTime ?? ""}
                  onChange={(e) =>
                    handleTimeChange(scheduleIndex, "endTime", e.target.value)
                  }
                  className="w-full"
                />
              </div>

              {/* Timezone - only show on first schedule entry */}
              {scheduleIndex === 0 &&
                props.timezones &&
                props.onTimezoneChange && (
                  <div className="space-y-1">
                    <Label className="flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5" />
                      Timezone
                    </Label>
                    <Select
                      value={props.timezoneId}
                      onValueChange={props.onTimezoneChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        {props.timezones.map((tz) => (
                          <SelectItem key={tz.id} value={tz.id}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
            </div>

            {/* Overnight shift warning */}
            {schedule.startTime &&
              schedule.endTime &&
              schedule.endTime <= schedule.startTime && (
                <div className="flex items-start p-3 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-700">
                  <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
                  <p>
                    Each day selected is when the shift starts. Shifts may end
                    on the following day.
                  </p>
                </div>
              )}

            {/* Short shift warning */}
            {schedule.startTime &&
              schedule.endTime &&
              (() => {
                const start = new Date(`1970-01-01T${schedule.startTime}`);
                const end = new Date(`1970-01-01T${schedule.endTime}`);
                let durationHours =
                  (end.getTime() - start.getTime()) / (1000 * 60 * 60);

                if (durationHours < 0) {
                  durationHours += 24;
                }

                if (durationHours > 0 && durationHours < 4) {
                  return (
                    <div className="flex items-start p-3 rounded-md border border-blue-300 bg-blue-50 text-blue-700">
                      <Info className="h-5 w-5 mr-2 flex-shrink-0" />
                      <p>
                        This shift is less than 4 hours. Please double-check for
                        mistakes.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}

            {/* On-Call and Recurrence Section */}
            <div className="border-t pt-4 mt-4 space-y-4">
              {/* On-Call Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor={`${scheduleIndex}-onCall`}>On-Call</Label>
                </div>
                <Switch
                  id={`${scheduleIndex}-onCall`}
                  checked={schedule.isOnCall ?? false}
                  onCheckedChange={(checked) => {
                    const updatedSchedules = props.value.map((s, i) =>
                      i === scheduleIndex ? { ...s, isOnCall: checked } : s,
                    );
                    props.onChange(updatedSchedules);
                  }}
                />
              </div>

              {/* Recurrence Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor={`${scheduleIndex}-recurrence`}>
                    Repeat this schedule
                  </Label>
                </div>
                <Switch
                  id={`${scheduleIndex}-recurrence`}
                  checked={schedule.recurrence.enabled}
                  onCheckedChange={(checked) =>
                    handleRecurrenceToggle(scheduleIndex, checked)
                  }
                />
              </div>

              {schedule.recurrence.enabled && (
                <div className="space-y-4 pl-6">
                  {/* Frequency */}
                  <div className="flex items-center gap-3">
                    <Label className="w-20">Repeat</Label>
                    <Select
                      value={schedule.recurrence.freq}
                      onValueChange={(value: "DAILY" | "WEEKLY") =>
                        handleRecurrenceChange(scheduleIndex, "freq", value)
                      }
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DAILY">Daily</SelectItem>
                        <SelectItem value="WEEKLY">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Interval */}
                  <div className="flex items-center gap-3">
                    <Label className="w-20">Every</Label>
                    <Input
                      type="number"
                      min={1}
                      max={52}
                      value={schedule.recurrence.interval}
                      onChange={(e) =>
                        handleRecurrenceChange(
                          scheduleIndex,
                          "interval",
                          Math.max(1, parseInt(e.target.value) || 1),
                        )
                      }
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">
                      {schedule.recurrence.freq === "DAILY"
                        ? schedule.recurrence.interval === 1
                          ? "day"
                          : "days"
                        : schedule.recurrence.interval === 1
                          ? "week"
                          : "weeks"}
                    </span>
                  </div>

                  {/* Days of week (only for weekly) */}
                  {schedule.recurrence.freq === "WEEKLY" && (
                    <div className="space-y-2">
                      <Label>On days</Label>
                      <div className="flex flex-wrap gap-1">
                        {ALL_DAYS.map((day) => (
                          <Button
                            key={day}
                            type="button"
                            variant={
                              schedule.recurrence.byDay.includes(day)
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            className={cn(
                              "w-10 h-8 p-0",
                              schedule.recurrence.byDay.includes(day) &&
                                "bg-primary text-primary-foreground",
                            )}
                            onClick={() => handleDayToggle(scheduleIndex, day)}
                          >
                            {DAY_LABELS[day]}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* End condition */}
                  <div className="space-y-3">
                    <Label>Ends</Label>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          id={`${scheduleIndex}-end-count`}
                          name={`${scheduleIndex}-end-type`}
                          checked={schedule.recurrence.endType === "count"}
                          onChange={() =>
                            handleRecurrenceChange(
                              scheduleIndex,
                              "endType",
                              "count",
                            )
                          }
                          className="h-4 w-4"
                        />
                        <Label
                          htmlFor={`${scheduleIndex}-end-count`}
                          className="font-normal"
                        >
                          After
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          max={365}
                          value={schedule.recurrence.count}
                          onChange={(e) =>
                            handleRecurrenceChange(
                              scheduleIndex,
                              "count",
                              Math.max(1, parseInt(e.target.value) || 1),
                            )
                          }
                          className="w-20"
                          disabled={schedule.recurrence.endType !== "count"}
                        />
                        <span className="text-sm text-muted-foreground">
                          {schedule.recurrence.freq === "WEEKLY" &&
                          schedule.recurrence.byDay.length > 1
                            ? schedule.recurrence.count === 1
                              ? "week"
                              : "weeks"
                            : "occurrences"}
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          id={`${scheduleIndex}-end-until`}
                          name={`${scheduleIndex}-end-type`}
                          checked={schedule.recurrence.endType === "until"}
                          onChange={() =>
                            handleRecurrenceChange(
                              scheduleIndex,
                              "endType",
                              "until",
                            )
                          }
                          className="h-4 w-4"
                        />
                        <Label
                          htmlFor={`${scheduleIndex}-end-until`}
                          className="font-normal"
                        >
                          On
                        </Label>
                        <SingleDatePicker
                          date={
                            schedule.recurrence.until
                              ? parse(
                                  schedule.recurrence.until,
                                  "yyyy-MM-dd",
                                  new Date(),
                                )
                              : undefined
                          }
                          onDateChange={(date) =>
                            handleRecurrenceChange(
                              scheduleIndex,
                              "until",
                              date ? format(date, "yyyy-MM-dd") : null,
                            )
                          }
                          disabled={schedule.recurrence.endType !== "until"}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Recurrence summary */}
                  {schedule.recurrence.enabled && (
                    <div className="p-3 rounded-md bg-muted/50 text-sm text-muted-foreground">
                      <span className="font-medium">Summary: </span>
                      {getRRuleDescription(schedule) ||
                        "Configure recurrence options above"}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        onClick={handleAddSchedule}
        className="mt-4"
      >
        <Plus className="mr-2 h-4 w-4" /> Add More Dates
      </Button>
    </div>
  );
}
